import { Injectable, WritableSignal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { lastValueFrom } from 'rxjs';

import { IUserContextUser } from 'rev-shared/security/UserContext.Service';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { PushBus } from 'rev-shared/push/PushBus.Service';

import { IChatASTNode, IChatMessage, IChatState, IVideoAiChat, MessageAuthor } from './VideoAssistantTypes';

const STILL_WORKING_TIMEOUT_MS = 30000;
const SOMETHING_WRONG_TIMEOUT_MS = 200000;

@Injectable()
export class VideoAiAssistantService {

	public chatState: WritableSignal<IChatState> = signal({
		messages: [],
		isWaitingForChatResponse: false,
		showStillWorkingMessage: false
	});

	private chatSessionId: string;
	private somethingWrongTimer: NodeJS.Timeout;
	private stillWorkingTimer: NodeJS.Timeout;
	private unsubscribe: IUnsubscribe;

	constructor(
		public PushBus: PushBus,
		public http: HttpClient
	) { }

	public getConversation(videoId: string): void {
		this.cancelTimers();
		lastValueFrom(this.http.get<any>(`/media/videos/${videoId}/ai-chat`))
			.then((result: any) => {
				this.chatState.update(cs => ({
					...cs,
					messages: this.getMappedChatHistory(result.content.messages),
					isWaitingForChatResponse: !result.content.isActive
				}));
				if (!result.content.isActive) {
					this.startTimers();
				}
			}).catch(err => {
				console.error('AI Assistant getConversation error: ', err);
				this.cancelTimers();
				this.addErrorMessage();
			});
	}

	public clearConversation(videoId: string): void {
		lastValueFrom(this.http.delete(`/media/videos/${videoId}/ai-chat`))
			.then(() => {
				this.chatState.update(cs => ({
					...cs,
					messages: []
				}));
			}).catch(err => {
				console.error('AI Assistant clearConversation error: ', err);
				this.cancelTimers();
				this.addErrorMessage();
			});
	}

	public addUserMessage(message: string, when?: string): void {
		this.addMsg({
			author: MessageAuthor.USER,
			content: message,
			timestamp: when ? new Date(when) : new Date(),
		});
	}

	public addBotMessage(message: IChatASTNode[], when?: string): void {
		this.addMsg({
			author: MessageAuthor.BOT,
			content: message,
			timestamp: when ? new Date(when) : new Date()
		});
	}

	public addErrorMessage() {
		this.addMsg({
			author: MessageAuthor.BOT,
			content: [],
			isErrorMessage: true,
			timestamp: new Date()
		});
	}

	public clearSessionData() {
		this.cancelTimers();
		if (this.unsubscribe) {
			this.unsubscribe();
		}
		this.chatState.set({
			messages: [],
			isWaitingForChatResponse: false,
			showStillWorkingMessage: false
		});
		this.chatSessionId = undefined;
	}

	public getSessionId(video: any, user: IUserContextUser): string {
		const userId: string = user.id;
		if (!this.chatSessionId) {
			this.chatSessionId = `${video.id}_${userId}_${Date.now()}`;
		}
		return this.chatSessionId;
	}

	public submitAiChat(videoId: string, videoAiChat: IVideoAiChat) {
		lastValueFrom(this.http.post(`/media/videos/${videoId}/ai-chat`, videoAiChat))
			.then(() => {
				this.startTimers();
			}).catch(err => {
				console.error('AI Assistant error: ', err);
				this.cancelTimers();
				this.addErrorMessage();
			});
	}

	public subscribePush() {
		if (this.unsubscribe) {
			return;
		}
		this.unsubscribe = this.PushBus.subscribe(this.chatSessionId, 'Media.VideoAiChat', {
			VideoAiChatResponseReceived: res => {
				this.cancelTimers();
				if (res.error) {
					this.addErrorMessage();
					console.error('AI Assistant error: ', res.error);
					return;
				}
				const exists = this.chatState().messages.some(
					msg => msg.author === MessageAuthor.BOT && msg.timestamp === res.answeredWhen
				);
				if (!exists) {
					this.addBotMessage(res.answer, res.answeredWhen);
				}
			}
		});
	}

	private startTimers() {
		this.update({ isWaitingForChatResponse: true });

		this.stillWorkingTimer = setTimeout(() => this.update({
			showStillWorkingMessage: true
		}), STILL_WORKING_TIMEOUT_MS);

		this.somethingWrongTimer = setTimeout(() => {
			this.addErrorMessage();
			this.cancelTimers();
		}, SOMETHING_WRONG_TIMEOUT_MS);
	}

	private cancelTimers() {
		this.update({ isWaitingForChatResponse: false });
		clearTimeout(this.stillWorkingTimer);
		this.update({ showStillWorkingMessage: false });
		clearTimeout(this.somethingWrongTimer);
	}

	private update(update: Partial<IChatState>): void {
		this.chatState.update(cs => ({
			...cs,
			...update
		}));
	}

	private addMsg(message: IChatMessage): void {
		this.chatState.update(cs => ({
			...cs,
			messages: [...cs.messages, message]
		}));
	}

	private getMappedChatHistory(messages: any[]): IChatMessage[] {
		const mapped: IChatMessage[] = [];

		// One interaction consists of 4 messages:
		// 1. User message (question)
		// 2. Assistant tool use message (answer)
		// 3. User message (tool result message)
		// 4. Assistant message (closing conversation loop)
		// We only need to map the first two messages
		for (let i = 0; i < messages.length; i += 4) {
			const userMsg = messages[i];
			const assistantMsg = messages[i + 1];

			if (userMsg && userMsg.role === 'user' && userMsg.content?.[0]?.text) {
				mapped.push({
					author: MessageAuthor.USER,
					content: userMsg.content[0].text,
					timestamp: userMsg.when
				});
			}

			if (
				assistantMsg &&
				assistantMsg.role === 'assistant' &&
				assistantMsg.content?.[0]?.toolUse
			) {
				const nodes = assistantMsg.content[0].toolUse.input?.nodes ?? [];
				mapped.push({
					author: MessageAuthor.BOT,
					content: nodes,
					timestamp: assistantMsg.when
				});
			}
		}

		return mapped;
	}
}
